import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import NextHead from 'next/head';
import { useRouter } from 'next/router';

import SETTINGS from '../../settings';

const HEAD_KEY = {
  MIXPANEL: 'mixpanel',
  FBQ: 'fbq',
  PINTRK: 'pintrk',
  SHAREASALE: 'shareasale',
  GA: 'ga',
  GTAG: 'gtag',
  GTAG_SCRIPT: 'gtagScript',
  GROOVE: 'groove',
};

const Scripts = props => {
  const timeout = useRef();
  const [isLoaded, setIsLoaded] = useState(false);
  const router = useRouter();

  useEffect(() => {
    timeout.current = setTimeout(() => {
      props.onAppInit();
      setIsLoaded(true);
    }, 2000);

    return () => {
      clearTimeout(timeout.current);
    };
  }, []);

  useEffect(() => {
    if (isLoaded && props.onPageView) {
      props.onPageView(router.asPath);
    }
  }, [isLoaded, props.onPageView, router.asPath]);

  if (!isLoaded) {
    return null;
  }

  return (
    <NextHead>
      <script
        key={HEAD_KEY.MIXPANEL}
        type="text/javascript"
        async
        dangerouslySetInnerHTML={{
          __html: `
            if (!window.mixpanel) {
            (function(c,a){if(!a.__SV){var b=window;try{var d,m,j,k=b.location,f=k.hash;d=function(a,b){return(m=a.match(RegExp(b+"=([^&]*)")))?m[1]:null};f&&d(f,"state")&&(j=JSON.parse(decodeURIComponent(d(f,"state"))),"mpeditor"===j.action&&(b.sessionStorage.setItem("_mpcehash",f),history.replaceState(j.desiredHash||"",c.title,k.pathname+k.search)))}catch(n){}var l,h;window.mixpanel=a;a._i=[];a.init=function(b,d,g){function c(b,i){var a=i.split(".");2==a.length&&(b=b[a[0]],i=a[1]);b[i]=function(){b.push([i].concat(Array.prototype.slice.call(arguments,
            0)))}}var e=a;"undefined"!==typeof g?e=a[g]=[]:g="mixpanel";e.people=e.people||[];e.toString=function(b){var a="mixpanel";"mixpanel"!==g&&(a+="."+g);b||(a+=" (stub)");return a};e.people.toString=function(){return e.toString(1)+".people (stub)"};l="disable time_event track track_pageview track_links track_forms track_with_groups add_group set_group remove_group register register_once alias unregister identify name_tag set_config reset opt_in_tracking opt_out_tracking has_opted_in_tracking has_opted_out_tracking clear_opt_in_out_tracking people.set people.set_once people.unset people.increment people.append people.union people.track_charge people.clear_charges people.delete_user people.remove".split(" ");
            for(h=0;h<l.length;h++)c(e,l[h]);var f="set set_once union unset remove delete".split(" ");e.get_group=function(){function a(c){b[c]=function(){call2_args=arguments;call2=[c].concat(Array.prototype.slice.call(call2_args,0));e.push([d,call2])}}for(var b={},d=["get_group"].concat(Array.prototype.slice.call(arguments,0)),c=0;c<f.length;c++)a(f[c]);return b};a._i.push([b,d,g])};a.__SV=1.2;b=c.createElement("script");b.type="text/javascript";b.async=!0;b.src="undefined"!==typeof MIXPANEL_CUSTOM_LIB_URL?
            MIXPANEL_CUSTOM_LIB_URL:"https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js";d=c.getElementsByTagName("script")[0];d.parentNode.insertBefore(b,d)}})(document,window.mixpanel||[]);
            mixpanel.init('${SETTINGS.SCRIPTS.MIXPANEL_ID}');
            }`,
        }}
      />

      <script
        key={HEAD_KEY.FBQ}
        type="text/javascript"
        async
        dangerouslySetInnerHTML={{
          __html: `
            if (!window.fbq) {
            !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
            n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
            document,'script','https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${SETTINGS.SCRIPTS.FACEBOOK_PIXEL_ID}');
            }`,
        }}
      />

      <script
        key={HEAD_KEY.PINTRK}
        type="text/javascript"
        async
        dangerouslySetInnerHTML={{
          __html: `
            if (!window.pintrk) {
            !function(e){if(!window.pintrk){window.pintrk=function(){window.pintrk.queue.push(
            Array.prototype.slice.call(arguments))};var
            n=window.pintrk;n.queue=[],n.version="3.0";var
            t=document.createElement("script");t.async=!0,t.src=e;var
            r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r)}}("https://s.pinimg.com/ct/core.js");
            pintrk('load', '${SETTINGS.SCRIPTS.PINTERESET_PIXEL_ID}');
            }`,
        }}
      />

      <script
        key={HEAD_KEY.SHAREASALE}
        type="text/javascript"
        async
        dangerouslySetInnerHTML={{
          __html: `
            var shareasaleSSCID = shareasaleGetParameterByName("sscid"); function shareasaleSetCookie(e, a, r, s, t) { if (e && a) { var o, n = s ? "; path=" + s : "", i = t ? "; domain=" + t : "", l = ""; r && ((o = new Date).setTime(o.getTime() + r), l = "; expires=" + o.toUTCString()), document.cookie = e + "=" + a + l + n + i } } function shareasaleGetParameterByName(e, a) { a || (a = window.location.href), e = e.replace(/[\\[\\]]/g, "\\\\$&"); var r = new RegExp("[?&]" + e + "(=([^&#]*)|&|#|$)").exec(a); return r ? r[2] ? decodeURIComponent(r[2].replace(/\\+/g, " ")) : "" : null }
            shareasaleSSCID && shareasaleSetCookie("shareasaleSSCID", shareasaleSSCID, ${SETTINGS.SCRIPTS.SHAREASALE_ID}, "/");`,
        }}
      />

      <script
        key={HEAD_KEY.GA}
        type="text/javascript"
        async
        dangerouslySetInnerHTML={{
          __html: `
            if (!window.ga) {
            (function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
            (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
            m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
            })(window,document,'script','//www.google-analytics.com/analytics.js','ga');
            ga('create', '${SETTINGS.SCRIPTS.GOOGLE_ANALYTICS_ID}', 'auto');
            }`,
        }}
      />

      <script
        key={HEAD_KEY.GTAG}
        type="text/javascript"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${SETTINGS.SCRIPTS.GTAG_ID}`}
      />

      <script
        key={HEAD_KEY.GTAG_SCRIPT}
        type="text/javascript"
        async
        dangerouslySetInnerHTML={{
          __html: `
            if (!window.gtag) {
            window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${SETTINGS.SCRIPTS.GTAG_ID}');
            gtag('config', '${SETTINGS.SCRIPTS.GTAG_ID_ADS}');
            }`,
        }}
      />

      <script
        key={HEAD_KEY.GROOVE}
        type="text/javascript"
        async
        dangerouslySetInnerHTML={{
          __html: `
            if (!window.groove) {
            !function(e,t){if(!e.groove){var i=function(e,t){return Array.prototype.slice.call(e,t)},a={widget:null,loadedWidgets:{},classes:{Shim:null,Embeddable:function(){this._beforeLoadCallQueue=[],this.shim=null,this.finalized=!1;var e=function(e){var t=i(arguments,1);if(this.finalized){if(!this[e])throw new TypeError(e+"() is not a valid widget method");this[e].apply(this,t)}else this._beforeLoadCallQueue.push([e,t])};this.initializeShim=function(){a.classes.Shim&&(this.shim=new a.classes.Shim(this))},this.exec=e,this.init=function(){e.apply(this,["init"].concat(i(arguments,0))),this.initializeShim()},this.onShimScriptLoad=this.initializeShim.bind(this),this.onload=void 0}},scriptLoader:{callbacks:{},states:{},load:function(e,i){if("pending"!==this.states[e]){this.states[e]="pending";var a=t.createElement("script");a.id=e,a.type="text/javascript",a.async=!0,a.src=i;var s=this;a.addEventListener("load",(function(){s.states[e]="completed",(s.callbacks[e]||[]).forEach((function(e){e()}))}),!1);var n=t.getElementsByTagName("script")[0];n.parentNode.insertBefore(a,n)}},addListener:function(e,t){"completed"!==this.states[e]?(this.callbacks[e]||(this.callbacks[e]=[]),this.callbacks[e].push(t)):t()}},createEmbeddable:function(){var t=new a.classes.Embeddable;return e.Proxy?new Proxy(t,{get:function(e,t){return e instanceof a.classes.Embeddable?Object.prototype.hasOwnProperty.call(e,t)||"onload"===t?e[t]:function(){e.exec.apply(e,[t].concat(i(arguments,0)))}:e[t]}}):t},createWidget:function(){var e=a.createEmbeddable();return a.scriptLoader.load("groove-script","https://${SETTINGS.SCRIPTS.GROOVE_ID}.widget.cluster.groovehq.com/api/loader"),a.scriptLoader.addListener("groove-iframe-shim-loader",e.onShimScriptLoad),e}};e.groove=a}}(window,document);
            window.groove.widget = window.groove.createWidget();
            window.groove.widget.init('${SETTINGS.SCRIPTS.GROOVE_ID}', {});
            }`,
        }}
      />
    </NextHead>
  );
};

Scripts.propTypes = {
  onAppInit: PropTypes.func,
  onPageView: PropTypes.func,
};

Scripts.defaultProps = {
  onAppInit: () => {},
  onPageView: () => {},
};

export default Scripts;
